import { PlusOutlined } from "@ant-design/icons";
import { AktDateRangePicker } from "components/aktDatePicker";
import { formatDate } from "common/utils";
import { useState } from "react";
import styled from "styled-components";

const StyledInputRow = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  width: 100%;
`;

const StyledDateRangePicker = styled(AktDateRangePicker)`
  flex: 1;
`;

const StyledAddValueButton = styled.button`
  padding: 4px 8px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  height: 32px;

  &:hover {
    border-color: #1890ff;
    color: #1890ff;

    .anticon {
      color: inherit;
    }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    border-color: #d9d9d9;
    color: #d9d9d9;
  }
`;

const StyledPlusIcon = styled(PlusOutlined)`
  font-size: 12px;
`;

/**
 * Display component for date range values
 * Handles: arrays, date range object, single date
 */
function UdfDateRangeDisplay({ value }) {
  // Handle array of values
  if (Array.isArray(value)) {
    return (
      <>
        {value.map((val, index) => (
          <span key={index}>
            <UdfDateRangeDisplay value={val} />
            {index < value.length - 1 && ", "}
          </span>
        ))}
      </>
    );
  }

  // Handle date range object
  if (value && typeof value === "object" && value.start && value.end) {
    return (
      <>
        {formatDate(value.start)} - {formatDate(value.end)}
      </>
    );
  }

  // Fallback: single date
  return <>{formatDate(value)}</>;
}

/**
 * Date range input component for UDF filters
 * Note: Uses AktDateRangePicker which handles both inputs internally
 */
function UdfDateRangeEdit({ addValue, currentValue, setCurrentValue, canAddCurrentValue }) {
  const [dateRange, setDateRange] = useState(null);

  const handleDateChange = (range) => {
    setDateRange(range);
    if (range && range[0] && range[1]) {
      // Convert array format to object format expected by the API
      setCurrentValue({ start: range[0], end: range[1] });
    } else {
      setCurrentValue(null);
    }
  };

  const handleAddValue = () => {
    if (currentValue) {
      addValue(currentValue);
      setDateRange(null);
      setCurrentValue(null);
    }
  };

  return (
    <StyledInputRow>
      <StyledDateRangePicker
        placeholder={["Start date", "End date"]}
        value={dateRange}
        onChange={handleDateChange}
      />
      <StyledAddValueButton
        onClick={handleAddValue}
        disabled={!canAddCurrentValue}
        aria-label="Add date range"
      >
        <StyledPlusIcon />
      </StyledAddValueButton>
    </StyledInputRow>
  );
}

/**
 * Factory function that returns both Edit and Display components
 */
function UdfDateRange() {
  return {
    Edit: UdfDateRangeEdit,
    Display: UdfDateRangeDisplay,
  };
}

export default UdfDateRange;
